import { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { BaseModal } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import type { EstadiaCliente, TipoChamadoClienteEstadia } from '../../../models';
import { useEstadiaClienteMutations } from '../../../hooks/data';
import { TIPO_CHAMADO_CLIENTE_LABELS } from '../equipamentosListPage.definitions';

interface RegistrarChamadoClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  estadia: EstadiaCliente;
}

/**
 * Registra que o SI abriu um chamado no sistema do PRÓPRIO CLIENTE (fora do Radar) —
 * pedindo homologação/entrada em estoque, ou retirada/configuração de saída. O Radar só
 * guarda o número (sem link — nunca usado; sem data de abertura manual, sempre "agora");
 * quem abre o chamado de verdade é o SI, manualmente, no sistema do cliente.
 */
export default function RegistrarChamadoClienteModal({ isOpen, onClose, estadia }: RegistrarChamadoClienteModalProps) {
  const { user } = useAuth();
  const { atualizarEstadia } = useEstadiaClienteMutations();

  const [tipo, setTipo] = useState<TipoChamadoClienteEstadia>('homologacao_estoque');
  const [numero, setNumero] = useState('');

  const handleClose = () => {
    setTipo('homologacao_estoque');
    setNumero('');
    onClose();
  };

  const handleSalvar = () => {
    const novoChamado = {
      id: crypto.randomUUID(),
      tipo,
      numero: numero.trim() || null,
      dataAbertura: Timestamp.now(),
      abertoPor: { id: user?.collaboratorId ?? user?.uid ?? '', name: user?.displayName ?? user?.email ?? '' },
    };

    atualizarEstadia.mutate(
      { id: estadia.id, chamados: [...estadia.chamados, novoChamado] },
      { onSuccess: handleClose }
    );
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Registrar chamado no Cliente" maxWidth="max-w-md" fullHeight={false}>
      <div className="space-y-4 py-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo *</label>
          <select
            className="input-primary mt-1 w-full"
            value={tipo}
            onChange={(event) => setTipo(event.target.value as TipoChamadoClienteEstadia)}
          >
            {(Object.entries(TIPO_CHAMADO_CLIENTE_LABELS) as [TipoChamadoClienteEstadia, string][]).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Número do chamado</label>
          <input type="text" className="input-primary mt-1 w-full" value={numero} onChange={(event) => setNumero(event.target.value)} />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleSalvar} disabled={atualizarEstadia.isPending}>
            {atualizarEstadia.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
